<template>
  <Panel
    v-loading="isLoading"
    class="preview-panel"
    background="#f2f2f2"
  >
    <template #titlebar>
      <PanelToolbar
        class="titlebar"
        :breakpoints="toolbarBreakpoints"
      >
        <template
          v-if="!item.is_revoked"
          #left
        >
          <PublishedPreviewToolbar
            :item="item"
            :panelMode="mode"
            :showCodesPopup="showCodesPopup"
            @extend="extend(item.id)"
            @republish="republish(item.id)"
            @toggleCodesPopup="toggleCodesPopup"
            @submit="republishUnedited(item.id, $event)"
            @share="showShareModal"
          />
        </template>

        <template #right>
          <PsButton
            size="sm"
            title="Открыть новость"
            @click="emit('open', item.id)"
          >
            <PbIcon name="open-out" />
          </PsButton>

          <PsDropdown
            trigger="manual"
            :visible="showActionsMenu"
            :options="options.filter(option => !option.isHidden)"
            @select="onNewsAction"
            @clickOutside="toggleActionsMenu(false)"
          >
            <PsButton
              size="sm"
              title="Действия с новостью"
              @click="toggleActionsMenu(!showActionsMenu)"
            >
              <PbIcon name="menu-burger" />
            </PsButton>
          </PsDropdown>
        </template>

        <NewsHistoryModal
          v-if="newsActionDialogVisible && item.id"
          :id="item.id"
          v-model:show="newsActionDialogVisible"
        />

        <NewsRelationsModal
          v-if="relationsDialogVisible"
          v-model="relationsDialogVisible"
          :itemId="item.id"
          @open="emit('open', $event)"
        />
      </PanelToolbar>
    </template>

    <template #content>
      <div class="content">
        <PublishedItemView
          :newsItem="item"
          :version="item.version || {}"
          :searchMode="searchMode"
        />
      </div>
    </template>
  </Panel>
</template>

<script setup lang="ts">
import { ElNotification, vLoading } from 'element-plus'
import { useToggle } from '@vueuse/core'
import { computed, inject, ref } from 'vue'
import { noop } from 'lodash-es'
import PublishedPreviewToolbar from './PublishedPreviewToolbar.vue'
import Panel from '@/ContextTab/components/Panel/Panel.vue'
import PanelToolbar from '@/ContextTab/components/Toolbar/PanelToolbar.vue'
import PsButton from '@/ContextTab/components/UI/Button/PsButton.vue'
import PsDropdown from '@/ContextTab/components/UI/PsDropdown.vue'
import PbIcon from '@/ContextTab/components/UI/PsIcon.vue'
import PublishedItemView from '@/ContextTab/modules/news/published/components/PublishedItemView.vue'
import NewsHistoryModal from '@/components/domain/NewsHistoryModal.vue'
import NewsRelationsModal from '@/components/domain/NewsRelationsModal.vue'
import { useShareModal } from '@/ContextTab/modules/news/composables/useShareModal'
import { newsAction } from '@/ContextTab/modules/news/published/components/types'
import { ACCESS } from '@/lib/accessCodes'
import { useAccess } from '@/ContextTab/stores/auth/useAccess'
import { useNewsEditorStore } from '@/stores/newsEditor.ts'
import { NotificationHandlerKey } from '@/injectionKeys.ts'
import type { SizeBreakpoints } from '@/ContextTab/composables/types'
import type { PublishedPanelMode } from '@/api/generated/v2'
import type { NewsItemPublished } from '@/ContextTab/stores/types.ts'


const props = defineProps<{
  item: NewsItemPublished
  mode: PublishedPanelMode
  searchMode: boolean
}>()

const emit = defineEmits<{
  (e: 'open', value: number): void
}>()

const newsEditorStore = useNewsEditorStore()

const { onError } = inject(NotificationHandlerKey, { onError: noop })

const isLoading = ref(false)

const { userId, userHasAllPermissions } = useAccess()

const showCodesPopup = ref(false)
const showActionsMenu = ref(false)

const [newsActionDialogVisible, toggleNewsActionDialogVisible] = useToggle()
const [relationsDialogVisible, toggleRelationsDialogVisible] = useToggle()
const { showShareModal } = useShareModal()

function toggleActionsMenu(show: boolean) {
  showActionsMenu.value = show
  if (showActionsMenu.value && showCodesPopup.value) {
    showCodesPopup.value = false
  }
}

function toggleCodesPopup(show: boolean) {
  showCodesPopup.value = show
  if (showCodesPopup.value && showActionsMenu.value) {
    showActionsMenu.value = false
  }
}

async function extend(id: number) {
  isLoading.value = true
  try {
    const news = await newsEditorStore.extend(id)
    ElNotification.success('Создано расширение')
    newsEditorStore.openByNewsItem(news)
  } catch (e) {
    onError(e)
  } finally {
    isLoading.value = false
  }
}

async function republish(id: number) {
  isLoading.value = true
  try {
    const news = await newsEditorStore.republish(id)
    ElNotification.success('Создан повтор')
    newsEditorStore.openByNewsItem(news)
  } catch (e) {
    onError(e)
  } finally {
    isLoading.value = false
  }
}

async function revoke(id: number) {
  isLoading.value = true
  try {
    const news = await newsEditorStore.revoke(id)
    ElNotification.success('Создано аннулирование')
    newsEditorStore.openByNewsItem(news)
  } catch (e) {
    onError(e)
  } finally {
    isLoading.value = false
  }
}

async function republishUnedited(id: number, codeIds: number[]) {
  isLoading.value = true
  try {
    await newsEditorStore.republishUnedited(id, codeIds)
    ElNotification.success('Новость переопубликована без изменений')
  } catch (e) {
    onError(e)
  } finally {
    isLoading.value = false
  }
}

function onNewsAction(action: string) {
  switch (action as keyof typeof newsAction) {
    case 'showHistory':
      toggleNewsActionDialogVisible(true)
      break
    case 'revoke':
      revoke(props.item.id)
      break
    case 'showRelations':
      toggleRelationsDialogVisible(true)
      break
    case 'sendToAsoi':
      showShareModal({
        itemId: props.item.id,
        actionType: 'sendToAsoi',
        hasKeepOriginal: true,
      })
  }
}

const options = computed(() => [
  {
    label: newsAction.sendToAsoi.label,
    key: newsAction.sendToAsoi.key,
    isHidden: props.item.is_revoked,
  },
  {
    label: newsAction.showHistory.label,
    key: newsAction.showHistory.key,
  },
  {
    label: newsAction.showRelations.label,
    key: newsAction.showRelations.key,
  },
  {
    label: newsAction.revoke.label,
    key: newsAction.revoke.key,
    show:
        props.item.publisher?.id === userId.value
        || userHasAllPermissions([ACCESS.NEWS.REVOKE]),
    isHidden: props.item.is_revoked || props.item.parent_rel_type_id == 3,
  },
],
)

const toolbarBreakpoints: SizeBreakpoints = {
  xs: 0,
  sm: 280,
  md: 390,
  lg: 500,
  xl: 1000,
  xxl: 1200,
  xxxl: 1800,
}
</script>

<style scoped>
.preview-panel {
  --toolbar-shadow: none;

  & ::v-deep(.panel-toolbar) {
    border-bottom: 1px solid #c9d4e1;
  }

  .titlebar {
    position: relative;
  }

  .content {
    overflow-y: auto;
  }
}
</style>
